import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertCircle,
  HardDrive,
  Plus,
  RefreshCw,
  Settings,
  User,
  Send,
  BarChart3
} from 'lucide-react';

export default function CreatorHub() {
  const navigate = useNavigate();
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadCreators();
  }, []);

  const loadCreators = async () => {
    setLoading(true);
    setError('');

    try {
      const backend = window.__BACKEND_URL;
      if (!backend) {
        setCreators([]);
        return;
      }

      await fetch(`${backend}/api/storage/health`, { credentials: 'include' }).catch(() => {});

      const res = await fetch(`${backend}/api/creators`, {
        credentials: 'include'
      });

      if (!res.ok) {
        setError('Unable to load creators right now.');
        setCreators([]);
        return;
      }

      const data = await res.json();
      if (Array.isArray(data)) {
        setCreators(data);
      } else if (Array.isArray(data?.creators)) {
        setCreators(data.creators);
      } else {
        setCreators([]);
      }
    } catch (err) {
      console.error('Failed to fetch creators:', err);
      setError('Unable to load creators right now.');
      setCreators([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreator = (creatorId) => {
    if (creatorId) {
      localStorage.setItem('activeCreatorId', creatorId);
    }
    navigate('/app/create/workspace');
  };

  const handleManageCreator = (creatorId) => {
    if (creatorId) {
      navigate(`/app/account/creator-profile/${creatorId}`);
    } else {
      navigate('/app/account/creator-profile');
    }
  };

  const handleAddCreator = () => {
    navigate('/app/account/creator-profile');
  };

  const renderEmptyState = () => (
    <Card className="glass border-white/5" data-testid="creator-hub-empty-state">
      <CardContent className="py-16 flex flex-col items-center text-center gap-5">
        <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center">
          <User className="h-8 w-8 text-blue-400" />
        </div>
        <div>
          <p className="text-white text-xl font-semibold">No Creators Yet</p>
          <p className="text-sm text-[#8A93A6] mt-2 max-w-sm">
            Create your first creator workspace to begin.
          </p>
        </div>
        <Button onClick={handleAddCreator} data-testid="empty-add-creator-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Creator
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* Hierarchy breadcrumb */}
      <div className="mb-1" data-testid="creator-hub-breadcrumb">
        <p className="text-xs text-[#8A93A6] tracking-wide">
          <Link to="/app/create" className="hover:text-white transition-colors">Create</Link>
          <span className="mx-1.5 opacity-50">/</span>
          <span className="text-[#C0C7D4]">Creator Hub</span>
        </p>
      </div>

      {/* Header */}
      <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white" data-testid="creator-hub-title">Creator Hub</h1>
          <p className="text-[#8A93A6] mt-1">Select a creator to enter their workspace</p>
        </div>
        <Button onClick={handleAddCreator} data-testid="add-creator-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Creator
        </Button>
      </div>

      {/* Error state */}
      {error && (
        <div className="mb-6 flex items-center gap-3 rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-4 py-3 text-yellow-200">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-10 h-10 border-4 border-[#3A7BFF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : creators.length === 0 ? (
        renderEmptyState()
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {creators.map((creator) => (
            <Card
              key={creator.id || creator._id || creator.name}
              className="glass hover:bg-white/5 transition-all border-white/5"
              data-testid={`creator-card-${creator.id || creator._id}`}
            >
              <CardContent className="pt-6 space-y-5">
                {/* Avatar + Name + Handle */}
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center shrink-0">
                    <User className="h-6 w-6 text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-base font-semibold text-white truncate">
                      {creator.name || 'Unnamed Creator'}
                    </p>
                    <p className="text-xs text-[#8A93A6] truncate">
                      {creator.handle || creator.username || '@creator'}
                    </p>
                  </div>
                </div>

                {/* Connection Status Pills */}
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge
                      variant="outline"
                      className={
                        creator.storage_connected || creator.storageConnected
                          ? 'border-green-500/30 text-green-400 text-xs'
                          : 'border-yellow-500/30 text-yellow-400 text-xs'
                      }
                      data-testid="storage-status-badge"
                    >
                      <HardDrive className="h-3.5 w-3.5 mr-1" />
                      {creator.storage_connected || creator.storageConnected ? 'Storage Connected' : 'Storage Not Connected'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="border-blue-500/20 text-[#8A93A6] text-xs" data-testid="publishing-badge">
                      <Send className="h-3.5 w-3.5 mr-1" />
                      Publishing: 0 Connected
                    </Badge>
                    <Badge variant="outline" className="border-blue-500/20 text-[#8A93A6] text-xs" data-testid="analytics-badge">
                      <BarChart3 className="h-3.5 w-3.5 mr-1" />
                      Analytics: 0 Connected
                    </Badge>
                  </div>
                </div>

                {/* CTA Row */}
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleOpenCreator(creator.id || creator._id)}
                    className="flex-1"
                    data-testid="open-workspace-btn"
                  >
                    Open Workspace
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleManageCreator(creator.id || creator._id)}
                    className="border-white/10"
                    data-testid="creator-settings-btn"
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
